import { useMemo } from 'react'
import { User, Calendar, Clock } from 'lucide-react'
import type { InterviewNotesItem, InterviewQuestion } from '@bandos/shared'
import { format } from 'date-fns'
import {
  EmptyState,
  EmptyStateTitle,
  EmptyStateText,
  QuestionsGrid,
  MetaRow,
  MetaBadge,
} from '../../../survey/pages/SurveyInsightsPageStyles'
import { InterviewInsightCard } from './InterviewInsightCard'
import {
  useResponseNavigation,
  ResponseNavBar,
  ResponseHeader,
  ResponseTitle,
  ScrollPad,
} from '../../../../components/common/responseNavigation'

export interface InterviewIndividualResponsesViewProps {
  notes: InterviewNotesItem[]
  /** Current interview questions — used as a fallback when a note has no snapshot. */
  questions: InterviewQuestion[]
}

function formatDuration(seconds: number): string {
  if (seconds < 60) return `${seconds}s`
  return `~${Math.round(seconds / 60)}m`
}

export function InterviewIndividualResponsesView({
  notes,
  questions,
}: InterviewIndividualResponsesViewProps) {
  const total = notes.length
  const { currentIndex, goPrev, goNext } = useResponseNavigation(total)

  const currentNote = total > 0 ? notes[currentIndex] : null

  const meta = useMemo(() => {
    if (!currentNote) return null
    const name = currentNote.intervieweeName?.trim() || 'Anonymous'
    const date = format(new Date(currentNote.savedAt), 'MMM d, yyyy')
    return { name, date }
  }, [currentNote])

  if (total === 0) {
    return (
      <EmptyState>
        <EmptyStateTitle>No interviews recorded yet</EmptyStateTitle>
        <EmptyStateText>
          Record interviews to flip through individual transcripts here.
        </EmptyStateText>
      </EmptyState>
    )
  }

  if (!currentNote || !meta) return null

  const transcriptQuestions = currentNote.questionsSnapshot ?? questions

  return (
    <>
      <ResponseHeader>
        <ResponseTitle>
          Interview {currentIndex + 1} of {total}
          {meta.name !== 'Anonymous' ? ` — ${meta.name}` : ''}
        </ResponseTitle>
        <MetaRow style={{ marginBottom: 0 }}>
          {meta.name === 'Anonymous' && (
            <MetaBadge>
              <User size={13} />
              {meta.name}
            </MetaBadge>
          )}
          <MetaBadge>
            <Calendar size={13} />
            {meta.date}
          </MetaBadge>
          {currentNote.durationSeconds != null && currentNote.durationSeconds > 0 && (
            <MetaBadge>
              <Clock size={13} />
              {formatDuration(currentNote.durationSeconds)}
            </MetaBadge>
          )}
        </MetaRow>
      </ResponseHeader>

      <QuestionsGrid>
        {transcriptQuestions.map((question, i) => {
          const raw = currentNote.answers[i]
          const answer = raw === null || raw === undefined ? '' : String(raw)
          return (
            <InterviewInsightCard
              key={question.id ?? i}
              question={question}
              questionIndex={i}
              answers={answer.length > 0 ? [answer] : []}
              isPrinting={false}
            />
          )
        })}
      </QuestionsGrid>

      <ScrollPad />

      <ResponseNavBar
        currentIndex={currentIndex}
        total={total}
        onPrev={goPrev}
        onNext={goNext}
        itemNoun="interview"
      />
    </>
  )
}
